import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Alert, Button, Container, Spinner } from "reactstrap"
import { toast } from "react-toastify"
import PdfViewer from "components/PdfViewer"
import GuardedVideo from "./GuardedVideo"
import HtmlContentFrame from "./HtmlContentFrame"
import { useMustRead } from "components/MustRead/MustReadProvider"
import { getMyMustReadContent, saveMustReadProgress, completeMustRead } from "helpers/mustReadApi"
import { getDisplayStatus, getStatusMeta } from "helpers/mustReadStatus"
import {
  CONFIRM_BUTTON,
  UPDATED_NOTICE_BODY,
  confirmStatement,
  updatedNoticeHeading,
  versionLabel,
} from "helpers/mustReadCopy"
import "components/MustRead/mustRead.css"

const SAVE_INTERVAL_MS = 10000
const VIDEO_END_TOLERANCE_SECONDS = 2

const MustReadViewer = () => {
  const { itemId } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { data, refresh } = useMustRead()

  const [content, setContent] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [endReached, setEndReached] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const progressRef = useRef({ position: 0, maxReached: 0, totalUnits: null, dirty: false })

  const items = data?.items || []
  const index = items.findIndex((i) => i.id === itemId)
  const item = index >= 0 ? items[index] : null
  const prev = index > 0 ? items[index - 1] : null
  const next = index >= 0 && index < items.length - 1 ? items[index + 1] : null
  const startPosition = Number(params.get("position") || item?.lastPosition || 0)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setEndReached(false)
    setConfirmed(false)
    try {
      const c = await getMyMustReadContent(itemId)
      if (c.contentType === "html") {
        progressRef.current = { position: 0, maxReached: 0, totalUnits: 1, dirty: false }
        try {
          await saveMustReadProgress(itemId, {
            versionId: c.versionId,
            position: 0,
            maxReached: 0,
            totalUnits: 1,
          })
          await refresh()
        } catch {
          /* best-effort in-progress mark; confirm flush retries */
        }
      } else {
        progressRef.current = {
          position: startPosition,
          maxReached: item?.currentVersionId === c.versionId ? item?.maxReached || 0 : 0,
          totalUnits: null,
          dirty: false,
        }
      }
      setContent(c)
    } catch (e) {
      const status = e?.response?.status
      if (status === 410) setError("This content is unavailable. Please contact ColdBlock.")
      else if (status === 404) setError("This item is no longer required.")
      else setError("Could not load this item. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [itemId, refresh])

  useEffect(() => {
    load()
  }, [load])

  const flush = useCallback(async () => {
    const p = progressRef.current
    if (!content?.versionId || !p.dirty) return false
    p.dirty = false
    try {
      await saveMustReadProgress(itemId, {
        versionId: content.versionId,
        position: p.position,
        maxReached: p.maxReached,
        totalUnits: p.totalUnits,
      })
      return true
    } catch (e) {
      if (e?.response?.status === 409) load()
      return false
    }
  }, [content, itemId, load])

  useEffect(() => {
    const timer = setInterval(flush, SAVE_INTERVAL_MS)
    const onHide = () => document.hidden && flush()
    document.addEventListener("visibilitychange", onHide)
    return () => {
      clearInterval(timer)
      document.removeEventListener("visibilitychange", onHide)
      flush()
        .then((saved) => (saved ? refresh() : undefined))
        .catch(() => {})
    }
  }, [flush, refresh])

  const track = useCallback((position, maxReached, totalUnits) => {
    const p = progressRef.current
    p.position = position
    p.maxReached = Math.max(p.maxReached, maxReached)
    if (totalUnits) p.totalUnits = totalUnits
    p.dirty = true
  }, [])

  const handlePdfPage = useCallback(
    ({ page, totalPages }) => {
      track(page, page, totalPages)
      if (page >= totalPages) setEndReached(true)
    },
    [track]
  )

  const handleVideoProgress = useCallback(
    ({ position, maxReached, duration }) => {
      track(position, maxReached, duration)
      if (duration && maxReached >= duration - VIDEO_END_TOLERANCE_SECONDS) setEndReached(true)
    },
    [track]
  )

  const handleHtmlBottom = useCallback(() => {
    track(1, 1, 1)
    setEndReached(true)
  }, [track])

  useEffect(() => {
    setConfirmed(false)
  }, [itemId, content?.versionId])

  const isReviewed = item?.status === "reviewed"
  const canConfirm = endReached && confirmed && !isReviewed && !completing && !!content

  const confirm = async () => {
    setCompleting(true)
    try {
      progressRef.current.dirty = true
      await flush()
      await completeMustRead(itemId, content.versionId)
      await refresh()
      toast.success("Marked as reviewed")
      const nextUnread = items.find((i) => i.id !== itemId && i.status !== "reviewed")
      if (nextUnread) navigate(`/must-read/${nextUnread.id}`)
      else navigate("/must-read")
    } catch (e) {
      const code = e?.response?.data?.error
      if (code === "STALE_VERSION") {
        toast.info("This item was just updated. Reloading the latest version.")
        await refresh()
        load()
      } else if (code === "NOT_FINISHED") {
        toast.warning(e.response.data.message)
      } else {
        toast.error("Could not save your review. Please try again.")
      }
    } finally {
      setCompleting(false)
    }
  }

  const hint = useMemo(() => {
    if (isReviewed) return "You have reviewed this resource."
    if (endReached) return null
    if (content?.contentType === "video") return "Watch to the end to confirm."
    if (content?.contentType === "html") return "Scroll to the bottom to confirm."
    return "Reach the last page to confirm."
  }, [content, endReached, isReviewed])

  const displayStatus = getDisplayStatus(item)
  const meta = getStatusMeta(displayStatus)
  const version = item ? versionLabel(item.versionNumber, item.publishedAt) : null

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: 1100 }}>
        <Link to="/must-read" className="d-inline-flex align-items-center mb-3 text-decoration-none">
          <i className="bx bx-chevron-left" aria-hidden="true" /> Back to Onboarding
        </Link>

        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
          <div>
            <h1 className="mr-heading h4 mb-1">{item?.title || "Onboarding Resources"}</h1>
            {version && <p className="text-muted small mb-0">{version}</p>}
          </div>
          {item && (
            <span className={`mr-status ${meta.className}`}>
              <i className={`bx ${meta.icon}`} aria-hidden="true" />
              {meta.label}
            </span>
          )}
        </div>

        {displayStatus === "updated" && item && (
          <Alert color="info" fade={false}>
            <strong>{updatedNoticeHeading(item.title)}</strong>
            <div>{UPDATED_NOTICE_BODY}</div>
            {item.changeNote ? <div className="mt-2">What changed: {item.changeNote}</div> : null}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : error ? (
          <Alert color="warning" fade={false}>{error}</Alert>
        ) : content?.contentType === "pdf" ? (
          <PdfViewer file={content.url} initialPage={startPosition || 1} onPageChange={handlePdfPage} />
        ) : content?.contentType === "video" ? (
          <GuardedVideo
            src={content.url}
            startAt={startPosition}
            onProgress={handleVideoProgress}
            onEnded={() => setEndReached(true)}
          />
        ) : content?.contentType === "html" ? (
          <HtmlContentFrame html={content.html} onBottomReached={handleHtmlBottom} />
        ) : null}

        <div className="mr-viewer-actions">
          <div className="d-flex gap-2">
            <Button outline color="secondary" disabled={!prev} onClick={() => prev && navigate(`/must-read/${prev.id}`)}>
              Previous
            </Button>
            <Button outline color="secondary" disabled={!next} onClick={() => next && navigate(`/must-read/${next.id}`)}>
              Next
            </Button>
          </div>
          {!error && (
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {hint && <span className="text-muted small" id="mr-confirm-hint">{hint}</span>}
              {!isReviewed && (
                <>
                  <button
                    type="button"
                    id="mr-confirm-check"
                    className="d-inline-flex align-items-center gap-2 border-0 bg-transparent p-0 text-start"
                    aria-pressed={confirmed}
                    aria-describedby={hint ? "mr-confirm-hint" : undefined}
                    disabled={!endReached || completing}
                    onClick={() => setConfirmed((on) => !on)}
                    style={{ cursor: !endReached || completing ? "not-allowed" : "pointer" }}
                  >
                    <span
                      aria-hidden="true"
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        border: "1px solid #adb5bd",
                        background: confirmed ? "#06427a" : "#fff",
                        color: "#fff",
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      {confirmed ? "✓" : ""}
                    </span>
                    <span className="text-body">{confirmStatement(content?.contentType)}</span>
                  </button>
                  <Button
                    className="mr-btn-primary"
                    disabled={!canConfirm}
                    onClick={confirm}
                    aria-describedby="mr-confirm-hint"
                  >
                    {completing ? <Spinner size="sm" /> : CONFIRM_BUTTON}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        <p className="text-muted small mt-3 mb-0">
          Confirming records that you reviewed this item. Reviewing is not required to use the portal.
        </p>
      </Container>
    </div>
  )
}

export default MustReadViewer
